/**
 * 全屏 Markdown 编辑器 - 支持富文本编辑、公式、表格、Mermaid 图表实时预览
 * 左侧：编辑区（Markdown 源码）
 * 右侧：预览区（使用 MarkdownRenderer 实时渲染）
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import MermaidBlock from './MermaidBlock';
import { documentApi, contentApi, API_BASE_URL } from '../services/api';
import {
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface FullscreenEditorProps {
  /** 是否打开 */
  open: boolean;
  /** 章节标题 */
  title?: string;
  /** 初始内容 */
  initialContent: string;
  /** 保存回调 */
  onSave: (content: string) => void;
  /** 关闭回调 */
  onClose: () => void;
  /** Mermaid 默认显示模式 */
  mermaidDefaultView?: 'code' | 'chart';
  /** 上一章节回调（父组件负责保存并切换） */
  onPrevChapter?: () => void;
  /** 下一章节回调 */
  onNextChapter?: () => void;
  /** 是否存在上一章节（用于禁用按钮） */
  canPrev?: boolean;
  /** 是否存在下一章节 */
  canNext?: boolean;
  /** 章节位置提示，如 "第 3 / 20 章" */
  chapterLabel?: string;
}

/** 快速插入工具栏按钮 */
interface ToolbarButton {
  label: string;
  icon: string;
  insert: string;
  block?: boolean; // 是否为块级元素
}

const TOOLBAR_BUTTONS: ToolbarButton[] = [
  { label: '标题1', icon: 'H1', insert: '# ', block: true },
  { label: '标题2', icon: 'H2', insert: '## ', block: true },
  { label: '标题3', icon: 'H3', insert: '### ', block: true },
  { label: '粗体', icon: 'B', insert: '****', block: false },
  { label: '斜体', icon: 'I', insert: '**', block: false },
  { label: '列表', icon: '•', insert: '- ', block: true },
  { label: '编号', icon: '1.', insert: '1. ', block: true },
  { label: '表格', icon: '⊞', insert: '\n| 列1 | 列2 | 列3 |\n| --- | --- | --- |\n| 内容 | 内容 | 内容 |\n', block: true },
  { label: '代码', icon: '<>', insert: '\n```\n\n```\n', block: true },
  { label: '公式', icon: '𝑓', insert: '\n$$\n\n$$\n', block: true },
  { label: 'Mermaid', icon: '📊', insert: '\n```mermaid\ngraph TD\n  A[开始] --> B[结束]\n```\n', block: true },
  { label: '引用', icon: '❝', insert: '> ', block: true },
  { label: '链接', icon: '🔗', insert: '[]()', block: false },
  { label: '分割线', icon: '—', insert: '\n---\n', block: true },
];

const FullscreenEditor: React.FC<FullscreenEditorProps> = ({
  open,
  title,
  initialContent,
  onSave,
  onClose,
  mermaidDefaultView = 'chart',
  onPrevChapter,
  onNextChapter,
  canPrev,
  canNext,
  chapterLabel,
}) => {
  const [content, setContent] = useState(initialContent);
  // 预览使用防抖后的内容，避免频繁重新渲染 Mermaid 图表
  const [previewContent, setPreviewContent] = useState(initialContent);
  const [splitRatio, setSplitRatio] = useState(50); // 编辑区占比百分比
  const [isDragging, setIsDragging] = useState(false);
  const [showPreviewOnly, setShowPreviewOnly] = useState(false);
  const [syncScroll, setSyncScroll] = useState(true); // 左右跟随滚动开关
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  // 图库相关
  const [allImages, setAllImages] = useState<Array<{ url: string; filename: string; size: number; created_at: number }>>([]);
  const [showGallery, setShowGallery] = useState(false);
  const galleryLoadedRef = useRef(false);
  // AI Mermaid 生成相关状态
  const [showGenerateMermaid, setShowGenerateMermaid] = useState(false);
  const [generateMermaidStep, setGenerateMermaidStep] = useState<'prompt' | 'preview'>('prompt');
  const [generateMermaidLoading, setGenerateMermaidLoading] = useState(false);
  const [generateMermaidTab, setGenerateMermaidTab] = useState<'context' | 'manual'>('context');
  const [generateMermaidContextText, setGenerateMermaidContextText] = useState('');
  const [generateMermaidManualText, setGenerateMermaidManualText] = useState('');
  // Step 2: 预览编辑
  const [generatedMermaidCode, setGeneratedMermaidCode] = useState('');
  const [editingMermaidCode, setEditingMermaidCode] = useState('');
  const mermaidPreviewCodeRef = useRef(''); // 防抖用
  const [mermaidPreviewCode, setMermaidPreviewCode] = useState(''); // 给 MermaidBlock 用的防抖值
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 滚动同步：防止循环触发
  const isSyncingRef = useRef(false);

  // 左侧编辑区滚动 → 同步右侧预览区
  const handleEditorScroll = useCallback(() => {
    if (!syncScroll || isSyncingRef.current || !editorRef.current || !previewRef.current) return;
    isSyncingRef.current = true;

    const editor = editorRef.current;
    const preview = previewRef.current;

    // 计算编辑区滚动比例
    const editorScrollTop = editor.scrollTop;
    const editorMaxScroll = editor.scrollHeight - editor.clientHeight;
    if (editorMaxScroll <= 0) { isSyncingRef.current = false; return; }
    const editorRatio = editorScrollTop / editorMaxScroll;

    // 同步到预览区
    const previewMaxScroll = preview.scrollHeight - preview.clientHeight;
    preview.scrollTop = editorRatio * previewMaxScroll;

    requestAnimationFrame(() => { isSyncingRef.current = false; });
  }, [syncScroll]);

  // 右侧预览区滚动 → 同步左侧编辑区
  const handlePreviewScroll = useCallback(() => {
    if (!syncScroll || isSyncingRef.current || !previewRef.current || !editorRef.current) return;
    isSyncingRef.current = true;

    const preview = previewRef.current;
    const editor = editorRef.current;

    // 计算预览区滚动比例
    const previewScrollTop = preview.scrollTop;
    const previewMaxScroll = preview.scrollHeight - preview.clientHeight;
    if (previewMaxScroll <= 0) { isSyncingRef.current = false; return; }
    const previewRatio = previewScrollTop / previewMaxScroll;

    // 同步到编辑区
    const editorMaxScroll = editor.scrollHeight - editor.clientHeight;
    editor.scrollTop = previewRatio * editorMaxScroll;

    requestAnimationFrame(() => { isSyncingRef.current = false; });
  }, [syncScroll]);

  // 同步初始内容 & 加载图库
  useEffect(() => {
    if (open) {
      setContent(initialContent);
      setPreviewContent(initialContent);
      setShowPreviewOnly(false);
      setShowGallery(false);
      // 打开编辑器时加载图片库
      loadImages();
    }
  }, [open, initialContent]);

  // 加载图片库
  const loadImages = useCallback(async () => {
    try {
      const res = await documentApi.listImages();
      setAllImages(res.data.images || []);
    } catch {
      // 静默失败
    }
  }, []);

  // 防抖更新预览内容（500ms 延迟），避免每敲一个字符都重绘 Mermaid
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewContent(content);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [content]);

  // AI Mermaid Step 2：编辑代码时的预览防抖（300ms）
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mermaidPreviewCodeRef.current !== editingMermaidCode) {
        mermaidPreviewCodeRef.current = editingMermaidCode;
        setMermaidPreviewCode(editingMermaidCode);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [editingMermaidCode]);

  // ESC 关闭
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      // Ctrl/Cmd+S 保存（不关闭编辑器）
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        onSave(content);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, content, onSave, onClose]);

  // 拖拽分隔线调整左右比例
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = Math.max(20, Math.min(80, (x / rect.width) * 100));
      setSplitRatio(ratio);
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // 插入文本到光标位置
  const insertAtCursor = useCallback((insertText: string, isBlock: boolean = false) => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = content.substring(0, start);
    const selected = content.substring(start, end);
    const after = content.substring(end);

    let newText: string;
    let cursorPos: number;

    if (isBlock) {
      // 块级插入：在当前行前插入
      const lineStart = before.lastIndexOf('\n') + 1;
      const beforeLine = before.substring(0, lineStart);
      const afterLine = before.substring(lineStart);
      newText = beforeLine + insertText + afterLine + selected + after;
      cursorPos = beforeLine.length + insertText.length;
    } else if (insertText.includes('**')) {
      // 内联样式：包裹选中文本
      const wrapper = insertText.replace('**', '');
      if (selected) {
        newText = before + wrapper + selected + wrapper + after;
        cursorPos = before.length + wrapper.length + selected.length + wrapper.length;
      } else {
        newText = before + insertText + after;
        cursorPos = before.length + insertText.length - 2;
      }
    } else if (insertText === '[]()' || insertText === '![]()') {
      if (selected) {
        newText = before + insertText.replace('[]', `[${selected}]`) + after;
      } else {
        newText = before + insertText + after;
      }
      cursorPos = before.length + insertText.length - 1;
    } else {
      newText = before + insertText + selected + after;
      cursorPos = before.length + insertText.length + selected.length;
    }

    setContent(newText);
    // 需要在下一个 tick 设置光标位置
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        editorRef.current.selectionStart = cursorPos;
        editorRef.current.selectionEnd = cursorPos;
      }
    }, 0);
  }, [content]);

  // 安全解析图片 URL
  const safeUrl = useCallback((rawUrl: string): string => {
    if (!rawUrl) return '';
    let url = rawUrl;
    // 去除末尾多余的逗号等特殊字符
    url = url.replace(/[,;，；]+$/, '');
    if (url.startsWith('http')) return url;
    return `${API_BASE_URL}${url}`;
  }, []);

  // 从图库选择并插入图片
  const handleGallerySelect = useCallback(
    (img: { url: string; filename: string }) => {
      const url = safeUrl(img.url);
      insertAtCursor(`![${img.filename || '图片'}](${url})`, false);
      setShowGallery(false);
    },
    [safeUrl, insertAtCursor],
  );

  // 上传图片并插入 Markdown 图片语法
  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 检查文件类型
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      alert('不支持的图片格式，支持: PNG, JPEG, GIF, WebP, SVG, BMP');
      return;
    }

    setUploadingImage(true);
    try {
      const response = await documentApi.uploadImage(file);
      if (response.data.success && response.data.url) {
        // 在光标位置插入 Markdown 图片语法，拼接完整 HTTP URL
        let fullUrl = response.data.url.startsWith('http') ? response.data.url : `${API_BASE_URL}${response.data.url}`;
        // 去除末尾可能多余的逗号等特殊字符
        fullUrl = fullUrl.replace(/[,;，；]+$/, '');
        const imageMarkdown = `![${file.name}](${fullUrl})`;
        insertAtCursor(imageMarkdown, false);
        // 刷新图库
        loadImages();
      } else {
        alert(response.data.message || '图片上传失败');
      }
    } catch (err) {
      console.error('图片上传失败:', err);
      alert('图片上传失败，请重试');
    } finally {
      setUploadingImage(false);
      // 清空 input 以允许重复上传同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [insertAtCursor, loadImages]);

  // AI Mermaid 生成：打开弹窗并捕获光标前后上下文
  const handleOpenGenerateMermaid = useCallback(() => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const contextBefore = content.substring(Math.max(0, cursorPos - 300), cursorPos);
    const contextAfter = content.substring(cursorPos, Math.min(content.length, cursorPos + 300));
    const contextPrompt = contextBefore + '\n[光标位置]\n' + contextAfter;

    setGenerateMermaidStep('prompt');
    setGenerateMermaidContextText(contextPrompt);
    setGenerateMermaidManualText('');
    setGenerateMermaidTab('context');
    setGeneratedMermaidCode('');
    setEditingMermaidCode('');
    setMermaidPreviewCode('');
    setShowGenerateMermaid(true);
  }, [content]);

  // 从 AI 返回内容中提取 mermaid 原始代码（去掉 ```mermaid 标记）
  const extractMermaidCode = useCallback((raw: string): string => {
    // 匹配 ```mermaid ... ``` 或 ```mermaid\n ... \n```
    const match = raw.match(/```mermaid\s*\n?([\s\S]*?)\n?\s*```/);
    if (match) return match[1].trim();
    // 如果没有代码块标记，直接返回原文
    return raw.trim();
  }, []);

  // AI Mermaid 生成：提交提词到后端 → 进入 Step 2 预览
  const handleSubmitGenerateMermaid = useCallback(async () => {
    const promptText = generateMermaidTab === 'context'
      ? generateMermaidContextText
      : generateMermaidManualText;

    if (!promptText.trim()) {
      alert('请输入或选择提词内容');
      return;
    }

    setGenerateMermaidLoading(true);
    try {
      const response = await contentApi.generateMermaid({ context: promptText.trim() });
      if (response.data.success && response.data.content) {
        const rawCode = extractMermaidCode(response.data.content);
        setGeneratedMermaidCode(rawCode);
        setEditingMermaidCode(rawCode);
        setMermaidPreviewCode(rawCode);
        mermaidPreviewCodeRef.current = rawCode;
        setGenerateMermaidStep('preview');
      } else {
        alert('Mermaid 图表生成失败，请重试');
      }
    } catch (err) {
      console.error('Mermaid 生成失败:', err);
      alert('Mermaid 图表生成失败，请检查网络连接后重试');
    } finally {
      setGenerateMermaidLoading(false);
    }
  }, [generateMermaidTab, generateMermaidContextText, generateMermaidManualText, extractMermaidCode]);

  // 返回上一步（回到提词输入）
  const handleBackToPrompt = useCallback(() => {
    setGenerateMermaidStep('prompt');
    setGeneratedMermaidCode('');
    setEditingMermaidCode('');
    setMermaidPreviewCode('');
    mermaidPreviewCodeRef.current = '';
  }, []);

  // Step 2：满意后插入图表到光标位置
  const handleInsertMermaid = useCallback(() => {
    const code = editingMermaidCode.trim();
    if (!code) {
      alert('请先编辑 Mermaid 代码');
      return;
    }
    const insertText = '\n```mermaid\n' + code + '\n```\n';
    insertAtCursor(insertText, true);
    setShowGenerateMermaid(false);
  }, [editingMermaidCode, insertAtCursor]);

  // Tab 键处理
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + '  ' + content.substring(end);
      setContent(newContent);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  // 保存（不关闭编辑器）
  const handleSave = useCallback(() => {
    setSaveStatus('saving');
    onSave(content);
    setSaveStatus('saved');
    // 2 秒后恢复为 idle
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      setSaveStatus('idle');
    }, 2000);
  }, [content, onSave]);

  // 上一章/下一章：先保存当前编辑内容，再切换
  const handlePrevChapter = useCallback(() => {
    if (!canPrev) return;
    onSave(content);
    onPrevChapter?.();
  }, [canPrev, onPrevChapter, onSave, content]);

  const handleNextChapter = useCallback(() => {
    if (!canNext) return;
    onSave(content);
    onNextChapter?.();
  }, [canNext, onNextChapter, onSave, content]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-gray-900">
      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between bg-gray-800 border-b border-gray-700 px-4 py-2 flex-shrink-0 select-none">
        {/* 左侧：标题 */}
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-medium text-gray-200">
            {title ? `编辑：${title}` : '全屏编辑'}
          </h2>
          <span className="text-xs text-gray-500">
            Ctrl+S 保存 · Esc 关闭
          </span>
          <span className="px-2 py-0.5 text-xs rounded bg-gray-700 text-blue-400 font-mono">
            当前字符数：{content.length} 字
          </span>
        </div>

        {/* 右侧：操作按钮 */}
        <div className="flex items-center gap-2">
          {/* 上一章/下一章导航 */}
          <button
            onClick={handlePrevChapter}
            disabled={!canPrev}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="上一章节"
          >
            上一章节
          </button>
          <button
            onClick={handleNextChapter}
            disabled={!canNext}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="下一章节"
          >
            下一章节
          </button>
          {chapterLabel && (
            <span className="px-1 text-xs text-gray-400 font-mono whitespace-nowrap">
              {chapterLabel}
            </span>
          )}
          <div className="w-px h-5 bg-gray-600 mx-1" />

          {/* 预览模式切换 */}
          <div className="flex items-center bg-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setShowPreviewOnly(false)}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${
                !showPreviewOnly ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
              title="分屏编辑"
            >
              <PencilIcon className="w-3.5 h-3.5" />
              <span>编辑</span>
            </button>
            <button
              onClick={() => setShowPreviewOnly(true)}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${
                showPreviewOnly ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
              title="仅预览"
            >
              <EyeIcon className="w-3.5 h-3.5" />
              <span>预览</span>
            </button>
          </div>

          <div className="w-px h-5 bg-gray-600 mx-1" />

          {/* 左右跟随滚动开关（仅编辑模式显示） */}
          {!showPreviewOnly && (
            <label
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer select-none transition-colors"
              title={syncScroll ? '左右跟随滚动：是' : '左右跟随滚动：否'}
            >
              <input
                type="checkbox"
                checked={syncScroll}
                onChange={(e) => setSyncScroll(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-500 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className={syncScroll ? 'text-gray-200' : 'text-gray-500'}>
                同步滚动
              </span>
            </label>
          )}

          <div className="w-px h-5 bg-gray-600 mx-1" />

          <button
            onClick={handleSave}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            保存 (Ctrl+S)
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title="关闭 (Esc)"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 快速插入工具栏 */}
      {!showPreviewOnly && (
        <div className="flex items-center gap-0.5 bg-gray-850 border-b border-gray-700 px-3 py-1.5 overflow-x-auto flex-shrink-0 select-none"
          style={{ backgroundColor: '#1f2937' }}>
          {TOOLBAR_BUTTONS.map((btn) => (
            <button
              key={btn.label}
              onClick={() => insertAtCursor(btn.insert, btn.block)}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors whitespace-nowrap"
              title={btn.label}
            >
              {btn.icon}
            </button>
          ))}
          {/* AI Mermaid：根据上下文或提词生成图表 */}
          <button
            onClick={handleOpenGenerateMermaid}
            className="flex items-center gap-1 px-2 py-1 text-xs text-purple-400 hover:text-purple-300 hover:bg-gray-600 rounded transition-colors whitespace-nowrap"
            title="AI 生成 Mermaid 图表"
          >
            <SparklesIcon className="w-3.5 h-3.5" />
            AI图表
          </button>
          {/* 分割线 */}
          <span className="w-px h-4 bg-gray-600 mx-1" />
          {/* 上传图片按钮 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,image/bmp"
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors whitespace-nowrap disabled:opacity-50"
            title="上传图片"
          >
            {uploadingImage ? (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                上传中
              </span>
            ) : (
              <>
                <PhotoIcon className="w-3.5 h-3.5" />
                上传
              </>
            )}
          </button>
          {/* 图库选择按钮 */}
          <button
            onClick={() => setShowGallery(!showGallery)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors whitespace-nowrap ${
              showGallery
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-600'
            }`}
            title="从已上传图片库选择"
          >
            <PhotoIcon className="w-3.5 h-3.5" />
            图库
          </button>
        </div>
      )}

      {/* 图库选择面板 */}
      {showGallery && !showPreviewOnly && (
        <div className="border-b border-gray-700 bg-gray-800 px-3 py-2 flex-shrink-0 select-none">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">
              点击缩略图插入 · 共 {allImages.length} 张 · 也可直接上传新图片
            </span>
            <button
              onClick={() => setShowGallery(false)}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              关闭
            </button>
          </div>
          {allImages.length === 0 ? (
            <div className="text-xs text-gray-500 py-3 text-center">
              暂无上传的图片，点击"上传"按钮添加
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
              {allImages.map((img, idx) => (
                <div
                  key={img.url || idx}
                  onClick={() => handleGallerySelect(img)}
                  className="cursor-pointer border-2 border-gray-600 rounded overflow-hidden hover:border-blue-400 transition-colors flex-shrink-0 group relative"
                  title={img.filename}
                >
                  <img
                    src={safeUrl(img.url)}
                    alt={img.filename || `图片 ${idx + 1}`}
                    className="w-14 h-14 object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 主编辑区域 */}
      <div
        ref={containerRef}
        className={`flex-1 flex min-h-0 ${isDragging ? 'cursor-col-resize select-none' : ''}`}
      >
        {/* 左侧：Markdown 编辑区 */}
        {!showPreviewOnly && (
          <>
            <div style={{ width: `${splitRatio}%` }} className="flex flex-col min-h-0">
              <textarea
                ref={editorRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                onScroll={handleEditorScroll}
                className="flex-1 bg-gray-900 text-gray-100 p-4 font-mono text-sm leading-relaxed resize-none outline-none border-r border-gray-700"
                placeholder="在此输入 Markdown 内容..."
                spellCheck={false}
              />
            </div>
            {/* 分隔线 */}
            <div
              onMouseDown={handleDragStart}
              className="w-1.5 bg-gray-700 hover:bg-blue-500 cursor-col-resize flex-shrink-0 transition-colors relative group"
            >
              <div className="absolute inset-y-0 -left-1 -right-1" />
            </div>
          </>
        )}

        {/* 右侧：实时预览 */}
        <div
          ref={previewRef}
          onScroll={handlePreviewScroll}
          className="flex-1 min-w-0 overflow-y-auto bg-white"
          style={showPreviewOnly ? {} : { width: `${100 - splitRatio}%` }}
        >
          <div className="p-6 max-w-4xl mx-auto">
            {previewContent.trim() ? (
              <MarkdownRenderer
                content={previewContent}
                mermaidDefaultView={mermaidDefaultView}
                enableMermaid={true}
                enableMath={true}
              />
            ) : (
              <div className="flex items-center justify-center h-full min-h-[200px] text-gray-400 text-sm">
                在左侧输入 Markdown 内容，这里将实时预览...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部状态栏 */}
      <div className="flex items-center justify-between bg-gray-800 border-t border-gray-700 px-4 py-1 flex-shrink-0 select-none">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>Markdown</span>
          <span>{content.length} 字符</span>
          <span>{content.split('\n').length} 行</span>
          {saveStatus !== 'idle' && (
            <span className={`${saveStatus === 'saved' ? 'text-green-400' : 'text-yellow-400'}`}>
              {saveStatus === 'saving' ? '保存中...' : '✓ 已保存'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {!showPreviewOnly && (
            <span>编辑区 {splitRatio.toFixed(0)}% : 预览区 {(100 - splitRatio).toFixed(0)}%</span>
          )}
        </div>
      </div>

      {/* AI Mermaid 生成弹窗 */}
      {showGenerateMermaid && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60">
          <div className={`bg-gray-800 border border-gray-600 rounded-lg shadow-2xl max-h-[92vh] flex flex-col transition-all duration-200 ${
            generateMermaidStep === 'preview' ? 'w-[1600px]' : 'w-[1000px]'
          }`}>
            {/* 弹窗标题栏 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-purple-400" />
                <h3 className="text-sm font-medium text-gray-200">AI 生成 Mermaid 图表</h3>
                <span className="text-xs text-gray-500">
                  步骤 {generateMermaidStep === 'prompt' ? '1/2' : '2/2'}
                </span>
              </div>
              <button
                onClick={() => setShowGenerateMermaid(false)}
                className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* === Step 1: 输入提词 === */}
            {generateMermaidStep === 'prompt' && (
              <>
                {/* Tab 切换 */}
                <div className="flex border-b border-gray-700 flex-shrink-0">
                  <button
                    onClick={() => setGenerateMermaidTab('context')}
                    className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
                      generateMermaidTab === 'context'
                        ? 'text-purple-400 border-b-2 border-purple-400'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-750'
                    }`}
                    style={generateMermaidTab === 'context' ? { backgroundColor: '#1f2937' } : {}}
                  >
                    上下文提词
                  </button>
                  <button
                    onClick={() => setGenerateMermaidTab('manual')}
                    className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
                      generateMermaidTab === 'manual'
                        ? 'text-purple-400 border-b-2 border-purple-400'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-750'
                    }`}
                    style={generateMermaidTab === 'manual' ? { backgroundColor: '#1f2937' } : {}}
                  >
                    手动输入提词
                  </button>
                </div>

                {/* Tab 内容 */}
                <div className="flex-1 p-4 min-h-0 overflow-y-auto">
                  {generateMermaidTab === 'context' ? (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        已自动提取光标位置前后各 300 字（共最多 600 字）作为上下文提词，可编辑后提交：
                      </p>
                      <textarea
                        value={generateMermaidContextText}
                        onChange={(e) => setGenerateMermaidContextText(e.target.value)}
                        className="w-full h-80 bg-gray-900 text-gray-100 border border-gray-600 rounded p-3 font-mono text-xs leading-relaxed resize-none outline-none focus:border-purple-500"
                        placeholder="光标上下文将在此显示..."
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        当前 {generateMermaidContextText.length} 字
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        手动输入提词描述，AI 将根据描述生成合适的 Mermaid 图表：
                      </p>
                      <textarea
                        value={generateMermaidManualText}
                        onChange={(e) => setGenerateMermaidManualText(e.target.value)}
                        className="w-full h-80 bg-gray-900 text-gray-100 border border-gray-600 rounded p-3 font-mono text-xs leading-relaxed resize-none outline-none focus:border-purple-500"
                        placeholder="例如：生成一个展示用户登录流程的时序图，包含用户、前端、后端、数据库四个参与者..."
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        当前 {generateMermaidManualText.length} 字
                      </p>
                    </div>
                  )}
                </div>

                {/* Step 1 底部按钮 */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 flex-shrink-0">
                  <span className="text-xs text-gray-500">
                    输入提词后点击"下一步"生成图表
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowGenerateMermaid(false)}
                      className="px-3 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSubmitGenerateMermaid}
                      disabled={generateMermaidLoading}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generateMermaidLoading ? (
                        <>
                          <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          生成中...
                        </>
                      ) : (
                        <>
                          下一步
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* === Step 2: 预览编辑 === */}
            {generateMermaidStep === 'preview' && (
              <>
                {/* 提示条 */}
                <div className="flex-shrink-0 px-4 py-2 bg-gray-750 border-b border-gray-700" style={{ backgroundColor: '#1a2332' }}>
                  <p className="text-xs text-gray-400">
                    左侧编辑代码，右侧实时预览。确认满意后点击"插入图表"将代码插入光标位置。
                  </p>
                </div>

                {/* 编辑 + 预览 左右分栏 */}
                <div className="flex-1 flex min-h-0">
                  {/* 左侧：代码编辑区 */}
                  <div className="w-1/2 flex flex-col border-r border-gray-700 min-h-0">
                    <div className="px-3 py-1.5 border-b border-gray-700 flex-shrink-0">
                      <span className="text-xs text-gray-400 font-medium">Mermaid 代码</span>
                    </div>
                    <textarea
                      value={editingMermaidCode}
                      onChange={(e) => setEditingMermaidCode(e.target.value)}
                      className="flex-1 bg-gray-900 text-gray-100 p-3 font-mono text-xs leading-relaxed resize-none outline-none"
                      placeholder="Mermaid 代码将在此显示..."
                      spellCheck={false}
                    />
                  </div>

                  {/* 右侧：实时预览 */}
                  <div className="w-1/2 flex flex-col min-h-0 bg-white">
                    <div className="px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                      <span className="text-xs text-gray-500 font-medium">实时预览</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3">
                      {mermaidPreviewCode.trim() ? (
                        <MermaidBlock chart={mermaidPreviewCode} defaultView="chart" />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                          编辑左侧代码后，图表将在此实时预览...
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 2 底部按钮 */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 flex-shrink-0">
                  <span className="text-xs text-gray-500">
                    代码编辑中可随时查看右侧预览效果
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowGenerateMermaid(false)}
                      className="px-3 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleBackToPrompt}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                      上一步
                    </button>
                    <button
                      onClick={handleInsertMermaid}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
                    >
                      <SparklesIcon className="w-3.5 h-3.5" />
                      插入图表
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FullscreenEditor;
